<template>
    <div class="bg-white h-full relative">
        <a :href="url" class="overflow-hidden rounded-sm item body-text block" :class="{ loading: isLoading }">

            <skeleton height="14rem" v-if="isLoading" />
            <div v-else class="relative overflow-hidden">
                <div class="w-full overflow-hidden item-image" style="aspect-ratio: 4/3;">
                    <lazy-image>
                        <template #image="{ load }">
                            <img class="w-full h-full !object-cover" :src="featuredImage"
                                :alt="currentPost?.title" @load.once="load" />
                        </template>
                    </lazy-image>
                </div>

                <div class="absolute top-0 bottom-0 left-0 right-0 flex justify-end items-start p-1 item-overlay">

                </div>
            </div>

            <div class="flex flex-col gap-2 p-3">
                <div class="flex flex-col gap-1" v-if="isLoading">
                    <skeleton />
                    <skeleton width="5rem" />
                </div>
                <div v-else>
                    <h6 class="!font-semibold body-text m-0 line-clamp-2" style="line-height: 1.3em">
                        {{ currentPost?.title }}
                    </h6>
                </div>

                <skeleton v-if="isLoading" height="3rem" />
                <p v-else-if="currentPost?.excerpt" class="text-sm text-gray-600 line-clamp-3 m-0">
                    {{ currentPost.excerpt }}
                </p>

                <div v-if="!isLoading && currentPost?.created_at" class="text-xs text-gray-500 mt-1">
                    {{ formatDate(currentPost.created_at) }}
                </div>

                <div v-if="!isLoading" class="flex justify-end mt-2">
                    <span class="text-xs text-primary uppercase font-medium flex items-center gap-1">
                        Read More
                        <span class="pi pi-chevron-right" style="font-size: 8px"></span>
                    </span>
                </div>
            </div>
        </a>
    </div>
</template>

<script lang="ts" setup>
import { useApiProxy } from '@/Hook/useProxy';
import { Post } from '@/types';
import { computed, onMounted } from 'vue';
import { Skeleton } from 'primevue';
import LazyImage from '@/Components/LazyImage.vue';
import { usePage } from '@inertiajs/vue3';
import { replaceS3WithCloudfront } from '@/utils/helpers';

const props = defineProps<{
    post?: Post;
    slug?: string;
    cache?: boolean;
    // Force the skeleton state from a parent (e.g. a listing awaiting results).
    loading?: boolean;
    // Server-resolved post, injected by ShortcodeResolver for the `[post ...]`
    // shortcode so it renders during SSR.
    ssrData?: { post?: Post };
}>();

const { data, loading: fetching, request } = useApiProxy<Post>();

// Seed the fetched-post ref from the server payload so `currentPost` resolves
// during SSR and the mount fetch is skipped.
if (props.ssrData?.post) data.value = props.ssrData.post;

const isLoading = computed(() => {
    if (props.loading) return true;
    if (props.post) return false;
    return fetching.value;
});

const page = usePage();

const currentPost = computed(() => {
    return props.post ?? data.value;
});

const featuredImage = computed(() => {
    const img = currentPost.value?.featured_image;
    if (!img) return undefined;
    return replaceS3WithCloudfront(img, page.props.__awsBucket as string, page.props.__awsUrl as string);
});

const url = computed(() => {
    if (!currentPost.value) return '#';

    if (currentPost.value.primary_category) {
        return route('show.post.with.category', {
            post_category: currentPost.value.primary_category.slug,
            post: currentPost.value.slug
        });
    }
    // Category-less posts have no category prefix; their only home is the bare
    // /inspiration/<slug>, which the category route renders directly.
    return route('show.category', { post_category: currentPost.value.slug });
});

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

onMounted(() => {
    if (!props.post && !props.ssrData?.post && props.slug) {
        const requestUrl = route('get.posts', { post: props.slug })
        request(requestUrl, {}, props.cache);
    }
});
</script>

<style lang="scss" scoped>
.item {
    .item-image {
        transform: scale(1);
        transition: 0.25s;
    }

    &.loading {
        cursor: wait !important;
    }

    &:hover {
        transition: 0.1s;
        cursor: pointer;

        .item-image {
            transform: scale(1.1);
            transition: 0.5s;
        }
    }
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
